/*
JWKS cache for verifying access JWTs in the UX.

Behaviour:
  * `getKey(kid)` returns a CryptoKey-like object usable with jose.jwtVerify.
  * On a kid-miss, refetches the JWKS once. Subsequent kid-misses inside the
    cooldown window throw without hitting the network.
  * `refreshJwks()` always fetches fresh keys, ignoring the TTL.
  * `_resetForTests()` clears all in-memory state and the localStorage cache.

The cached set lives in `localStorage.xolas-jwks` so it survives reloads
(public keys only — safe to persist).
------------------------------------------------------------------------ */

import jose from '@/lib/jose-shim'
import { JWKS_URL, API_KEY } from '@/lib/env'

const STORAGE_KEY = 'xolas-jwks'
const TTL_MS = 6 * 60 * 60 * 1000 // 6 hours
const COOLDOWN_MS = 10 * 1000 // 10 seconds between kid-miss refetches

let inMemoryJwks = null // { keys, fetchedAt }
let localSet = null
let lastRefetchAttemptAt = 0
let inFlightFetch = null

function safeLocalStorage () {
  try {
    return (typeof window !== 'undefined' && window.localStorage) ? window.localStorage : null
  } catch (e) {
    return null
  }
}

function readFromStorage () {
  const ls = safeLocalStorage()
  if (!ls) {
    return null
  }
  try {
    const raw = ls.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (!parsed || !parsed.keys || !parsed.fetchedAt) {
      return null
    }
    return parsed
  } catch (e) {
    return null
  }
}

function writeToStorage (cached) {
  const ls = safeLocalStorage()
  if (!ls) {
    return
  }
  try {
    ls.setItem(STORAGE_KEY, JSON.stringify(cached))
  } catch (e) {
    // ignore quota / private mode failures
  }
}

function isFresh (cached) {
  if (!cached || !cached.fetchedAt) {
    return false
  }
  return (Date.now() - cached.fetchedAt) < TTL_MS
}

function hydrateFromStorage () {
  if (inMemoryJwks) {
    return
  }
  const cached = readFromStorage()
  if (cached && isFresh(cached)) {
    inMemoryJwks = cached
    localSet = jose.createLocalJWKSet({ keys: cached.keys })
  }
}

function buildSet (keys) {
  return jose.createLocalJWKSet({ keys: keys })
}

export function refreshJwks () {
  if (inFlightFetch) {
    return inFlightFetch
  }
  inFlightFetch = fetch(JWKS_URL, {
    method: 'GET',
    // Bypass the browser HTTP cache: `.well-known/jwks.json` can be served with
    // a Cache-Control max-age, and without this a refetch after a signing-key
    // rotation would return the stale (old-kid) set, so verification of a freshly
    // issued token keeps failing. A refresh must always hit the network.
    cache: 'no-store',
    credentials: 'omit',
    headers: { 'X-API-Key': API_KEY }
  })
    .then(function (resp) {
      if (!resp.ok) {
        throw new Error('JWKS fetch failed: ' + resp.status)
      }
      return resp.json()
    })
    .then(function (body) {
      if (!body || !Array.isArray(body.keys)) {
        throw new Error('JWKS response missing keys array')
      }
      inMemoryJwks = { keys: body.keys, fetchedAt: Date.now() }
      localSet = buildSet(body.keys)
      writeToStorage(inMemoryJwks)
      return inMemoryJwks
    })
    .then(function (result) {
      inFlightFetch = null
      return result
    }, function (err) {
      inFlightFetch = null
      throw err
    })
  return inFlightFetch
}

function hasKid (kid) {
  if (!inMemoryJwks || !inMemoryJwks.keys || !kid) {
    return false
  }
  for (let i = 0; i < inMemoryJwks.keys.length; i++) {
    if (inMemoryJwks.keys[i].kid === kid) {
      return true
    }
  }
  return false
}

/**
 * Resolve a verification key for the given JWT header.
 * Mirrors the jose RemoteJWKSet contract: callable as `(protectedHeader, token)`.
 *
 * @param {string|object} kidOrHeader  either the raw kid or a jose protectedHeader
 * @returns {Promise<CryptoKey | KeyLike>}
 */
export function getKey (kidOrHeader) {
  const header = (typeof kidOrHeader === 'string') ? { kid: kidOrHeader } : (kidOrHeader || {})
  const kid = header.kid

  hydrateFromStorage()

  return Promise.resolve()
    .then(function () {
      if (!localSet || !inMemoryJwks || !isFresh(inMemoryJwks)) {
        return refreshJwks().then(function () { return null })
      }
      return null
    })
    .then(function () {
      if (kid && !hasKid(kid)) {
        const now = Date.now()
        if ((now - lastRefetchAttemptAt) < COOLDOWN_MS) {
          const err = new Error('JWKSNoMatchingKey: kid "' + kid + '" not found and cooldown active')
          err.code = 'ERR_JWKS_NO_MATCHING_KEY'
          throw err
        }
        lastRefetchAttemptAt = now
        return refreshJwks().then(function () { return null })
      }
      return null
    })
    .then(function () {
      if (!localSet) {
        const err = new Error('JWKSNoMatchingKey: no JWKS available')
        err.code = 'ERR_JWKS_NO_MATCHING_KEY'
        throw err
      }
      return localSet(header)
    })
}

// Test-only reset. Gated so the helper is dropped from prod bundles
// when UglifyJS eliminates the unreachable branch (DefinePlugin
// inlines `process.env.NODE_ENV`).
export const _resetForTests = (process.env.NODE_ENV !== 'production')
  ? function _resetForTests () {
    inMemoryJwks = null
    localSet = null
    lastRefetchAttemptAt = 0
    inFlightFetch = null
    const ls = safeLocalStorage()
    if (ls) {
      try {
        ls.removeItem(STORAGE_KEY)
      } catch (e) {
        // ignore
      }
    }
  }
  : undefined

export default {
  getKey: getKey,
  refreshJwks: refreshJwks,
  _resetForTests: _resetForTests
}
